import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getCsrfToken, clearCsrfToken, setCsrfAuthenticated } from '../../utils/csrf';
import { getFriendlyErrorMessage } from '../../utils/errorMessage';
import { useAuth } from '@/context/AuthContext';
import {
  installToastGuard,
  beginSessionTeardown,
  endSessionTeardown,
  showSessionEndedNotice,
} from '@/utils/toastGuard';

// Patch the sonner singleton once, at module load, so toasts are gated even if
// something fires before the interceptor component mounts.
installToastGuard();


const isSessionGone = async (): Promise<boolean> => {
  try {
    const res = await axios.get('/api/method/orgain.user_api.get_user_details', {
      timeout: 10000,
    });
    return res.data?.message?.status !== 'success' || !res.data?.message?.data;
  } catch {
   // logout - a transient network error shouldn't eject the user.
    return false;
  }
};

/**
 * Sets up global axios interceptors:
 *  - Request:  inject X-Frappe-CSRF-Token on POST/PUT/PATCH/DELETE so
 *              Frappe's CSRF validator (which runs on every mutating
 *              method) accepts the call.
 *  - Response: redirect to /login on 401 for non-auth endpoints.
 *
 * Must be rendered inside <BrowserRouter> to access useNavigate.
 */
const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const SESSION_ENDED_MESSAGE =
  'Your session has ended. Your account may have been deactivated. Please sign in again.';

let redirectingToLogin = false;

export const AxiosInterceptor: React.FC = () => {
  const navigate = useNavigate();
  const { clearSession } = useAuth();
  // Keep a live ref so the interceptor closures (registered once) always call
  // the latest clearSession without re-registering on every auth change.
  const clearSessionRef = useRef(clearSession);
  clearSessionRef.current = clearSession;

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(async (config) => {
      const method = (config.method || 'get').toLowerCase();
      if (!MUTATING_METHODS.has(method)) return config;

      // The token endpoint is itself a GET (handled above), but guard
      // defensively in case callers ever POST to it.
      const url = config.url || '';
      if (url.includes('orgain.user_api.get_csrf_token')) return config;

      const token = await getCsrfToken();
      if (token) {
        config.headers = config.headers || {};
        (config.headers as Record<string, string>)['X-Frappe-CSRF-Token'] = token;
      }
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // A successful response proves the session is alive again; lift both
        // the redirect latch and the toast-suppression gate.
        redirectingToLogin = false;
        endSessionTeardown();
        return response;
      },
      async (error) => {
        // Self-heal a stale CSRF token. Frappe rotates the token whenever the
        // session changes (login, session expiry, backend restart); the cached
        // token then mismatches and every mutating request fails with
        // CSRFTokenError ("Invalid Request"). Rather than forcing the user to
        // refresh the page, clear the cache, fetch a fresh token, and retry the
        // request once. The refetch returns the CURRENT session's token, so the
        // retry succeeds transparently.
        const config = error?.config;
        const data = error?.response?.data;
        const isCsrfError =
          (data && typeof data === 'object' && data.exc_type === 'CSRFTokenError') ||
          (typeof data === 'string' && data.includes('CSRFTokenError'));
        if (isCsrfError && config && !(config as any)._csrfRetried) {
          (config as any)._csrfRetried = true;
          clearCsrfToken();
          const token = await getCsrfToken();
          if (token) {
            config.headers = config.headers || {};
            (config.headers as Record<string, string>)['X-Frappe-CSRF-Token'] = token;
          }
          return axios(config);
        }

        const status = error?.response?.status;

        const isCanceled = axios.isCancel(error) || error?.code === 'ERR_CANCELED';
        const noResponse =
          !isCanceled && !error?.response && (error?.request || error?.code === 'ERR_NETWORK');
        const isGatewayDown = status === 502 || status === 503 || status === 504;

        // Attach a user-friendly message to EVERY rejected request so any catch
        // handler that surfaces `error.message` (or the explicit
        // `error.friendlyMessage`) shows readable text instead of the raw
        // "Request failed with status code 502". For infra/transient failures
        // (gateway down, no response, timeout) the raw axios message carries no
        // useful information, so we also overwrite `error.message` itself - that
        // makes existing `toast(..., { description: error.message })` call sites
        // friendly app-wide without editing each one. The original is preserved
        // on `error.rawMessage` for logging/diagnostics.
        //
        // We deliberately do NOT pop a generic global toast here anymore: the
        // contextual per-action toasts ("Drive connect failed", "Failed to link
        // documents", …) now carry a clear description, and a second generic
        // toast would just double up.
        if (!isCanceled) {
          const friendly = getFriendlyErrorMessage(error);
          error.friendlyMessage = friendly;
          if (isGatewayDown || noResponse || error?.code === 'ECONNABORTED') {
            error.rawMessage = error.message;
            error.message = friendly;
          }
        }

        // Skip redirect for login/auth/session-check endpoints. The session
        // probe `get_user_details` is allow_guest and reports "no session"
        // via response body, not HTTP status - but other Frappe endpoints
        // may 401 when the probe hasn't yet established auth, and we must
        // not bounce the user before AuthContext has finished initializing.
        const url = error?.config?.url || '';
        const isAuthEndpoint =
          url.includes('/login') ||
          url.includes('/logout') ||
          url.includes('/forgot-password') ||
          url.includes('/reset-password') ||
          url.includes('get_user_details') ||
          url.includes('get_current_user');

        // If we're already on /login, don't redirect again (avoids loops).
        const alreadyOnLogin = window.location.pathname === '/login';

        
        const ejectToLogin = () => {
          redirectingToLogin = true;
          // Suppress the wall of contextual error toasts from the other
          // in-flight requests that are all about to 401/403 too, and show a
          // single clean notice instead.
          beginSessionTeardown();
          setCsrfAuthenticated(false);
          clearCsrfToken();
          // Drop client auth state FIRST so ProtectedRoute/LoginPage don't
          // bounce a still-"logged-in" user back to /dashboard (redirect
          // loop / flicker); then navigate.
          clearSessionRef.current();
          if (!isCanceled) {
            error.rawMessage = error.message;
            error.message = SESSION_ENDED_MESSAGE;
            error.friendlyMessage = SESSION_ENDED_MESSAGE;
            error.sessionEnded = true;
          }
          showSessionEndedNotice();
          navigate('/login', { replace: true });
        };

        if (!isAuthEndpoint && !alreadyOnLogin && !redirectingToLogin) {
          if (status === 401) {
            // Unauthenticated: session is definitively gone.
            ejectToLogin();
          } else if (status === 403 && (await isSessionGone())) {
            // A 403 only means "session gone" when the probe confirms Guest;
            ejectToLogin();
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [navigate]);

  return null;
};
